'use client'

import Image from 'next/image'
import React from 'react'

import { useLocale } from '@src/i18n/LocaleProvider'
import { getBuilderPreviewMode } from '@src/utils/builder.util'
import { getImage } from '@src/utils/product.util'
import { cn } from '@src/utils/tailwind'

interface FacetSelectorProps {
  facetKey: string
  onFacetSelected?: (facetSlug: string) => void
  facetOptions: Array<IFacetOption<Facet>>
}

export const FacetSelector = ({ facetKey, onFacetSelected, facetOptions }: FacetSelectorProps) => {
  const { locale } = useLocale('common')
  const { edit } = getBuilderPreviewMode()
  return (
    <div className="no-scrollbar -mx-4 flex max-w-screen gap-2 overflow-x-auto px-4 lg:max-w-full lg:flex-wrap">
      {facetOptions?.map((option, idx) => {
        const facetSlug = option?.slug
        const facetName = option?.displayName?.[locale.code]
        const facetImage = option?.imagePrimary && getImage(option?.imagePrimary, locale.code)
        const facetDescriptor = option?.descriptor?.[locale.code]
        return (
          <label
            data-testid={`${facetKey}Option${idx}`}
            className={cn(
              'flex shrink-0 cursor-pointer items-center rounded-lg border border-bodi-gray-butters p-3 outline-bodi-gray-butters transition-all hover:border-bodi-black',
              {
                'border-bodi-black border-2': option.isSelected,
                'border-dashed grayscale text-bodi-gray-disabled border-bodi-gray-disabled':
                  !option.isAvailable,
              }
            )}
            key={facetSlug}
          >
            <input
              type="radio"
              name="facet"
              value={facetSlug}
              className="hidden"
              checked={option.isSelected}
              onChange={() => {
                if (edit) return
                onFacetSelected?.(facetSlug)
              }}
            />
            {facetImage && (
              <Image
                className="mr-2 size-4 rounded-full bg-cover bg-center bg-no-repeat"
                src={facetImage.url}
                alt={facetImage.alt}
                width={16}
                height={16}
              />
            )}
            <div
              className={cn('text-body-sm', {
                'font-medium': option.isSelected,
              })}
            >
              {facetName ?? option.content}
            </div>
            {facetDescriptor && (
              <>
                <span className="px-1">{' — '}</span>
                <span
                  className="text-body-sm"
                  dangerouslySetInnerHTML={{ __html: facetDescriptor }}
                ></span>
              </>
            )}
          </label>
        )
      })}
    </div>
  )
}
